import { ExpandMore } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/green.css";
import AutoCompleteFilter from "../autoCompleteFilter/AutoCompleteFilter";
import { CustomTooltip } from "../tooltip/CustomToolTip";

export type FilterType =
  | "number"
  | "string"
  | "boolean"
  | "date"
  | "date-range"
  | "datetime-range"
  | "datetime-range-utc";

type DateFieldDataType = "date-range" | "datetime-range" | "datetime-range-utc";

export type FilterableColumns = {
  [key: string]: {
    displayName: string;
    options: string[];
    type: FilterType;
  };
};

export type FilterableColumnsState = {
  [key: string]: {
    values: string[];
    type: FilterType;
  };
};

type GridDataFilterProps = {
  title: string;
  dateRange?: DateObject[];
  resetFlag?: boolean;
  asyncSubmitFunction: CallableFunction;
  asyncSelectedColumnValues: CallableFunction;
  filterableColumns: FilterableColumns;
};

type FilterOptions = {
  columnName: string;
  inputRank: number | null;
};

const GridDataFilter = ({
  title,
  dateRange,
  resetFlag,
  asyncSubmitFunction,
  asyncSelectedColumnValues,
  filterableColumns,
}: GridDataFilterProps) => {
  const [open, setOpen] = useState(false);

  const [inputList, setInputList] = useState<FilterOptions[]>([
    {
      columnName: "",
      inputRank: null,
    },
  ]);

  const [clFilterableColumns, setClFilterableColumns] =
    useState<FilterableColumns>(filterableColumns);

  const [filterableColumnsState, setFilterableColumnsState] =
    useState<FilterableColumnsState>({});

  useEffect(() => {
    if (resetFlag) {
      handleReset(resetFlag);
    }
  }, [resetFlag]);

  useEffect(() => {
    setFilterableColumnsState((prevState) => {
      const newState: any = {};
      inputList.forEach((item) => {
        if (prevState.hasOwnProperty(item.columnName)) {
          newState[item.columnName] = prevState[item.columnName];
        }
      });
      return newState;
    });
  }, [inputList]);

  // Callbacks
  const handleAddFilter = () => {
    setInputList([
      ...inputList,
      {
        columnName: "",
        inputRank: null,
      },
    ]);
  };

  const handleRemoveFilter = (index: number) => {
    const currentState = { ...filterableColumnsState };
    delete currentState[inputList[index].columnName];
    setFilterableColumnsState(currentState);
    const updatedFilters = inputList.filter((_, i) => i !== index);
    setInputList(updatedFilters);
  };

  const handleColumnNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { value } = event.target;
    setInputList((prevInputList) => {
      return prevInputList.map((item, i) => {
        return i === index
          ? { ...item, columnName: value, inputRank: index }
          : item;
      });
    });
    asyncSelectedColumnValues(value, setClFilterableColumns);
  };

  const handleSelectedOption = (
    event: Event | undefined,
    columnName: string,
    value: string[],
    type: FilterType
  ) => {
    const currentState = { ...filterableColumnsState };
    if (currentState[columnName]) {
      if (value.length) {
        currentState[columnName].values = value;
        currentState[columnName].type = type;
      } else {
        delete currentState[columnName];
      }
    } else {
      currentState[columnName] = { values: value, type: type };
    }
    setFilterableColumnsState(currentState);
  };

  const handleReset = (submit: boolean) => {
    setFilterableColumnsState({});
    setClFilterableColumns(JSON.parse(JSON.stringify(filterableColumns)));
    setInputList([
      {
        columnName: "",
        inputRank: null,
      },
    ]);
    if (submit) {
      asyncSubmitFunction({});
    }
  };

  const isDateRangeSelected = Object.values(filterableColumnsState).some(
    (state) =>
      state.type === "date-range" ||
      state.type === "datetime-range" ||
      state.type === "datetime-range-utc"
  );

  const hasDateRangeType = Object.values(filterableColumnsState).some(
    (col) =>
      col.type === "date-range" ||
      col.type === "datetime-range" ||
      col.type === "datetime-range-utc"
  );

  const isLastFilterComplete = () => {
    const lastFilter = inputList[inputList.length - 1];
    const columnState = filterableColumnsState[lastFilter.columnName];
    return (
      lastFilter.columnName !== "" &&
      columnState &&
      columnState.values &&
      columnState.values.length > 0
    );
  };

  return (
    <div>
      <Accordion elevation={2} expanded={open}>
        <AccordionSummary
          onClick={() => {
            setOpen(!open);
          }}
          expandIcon={<ExpandMore sx={{ color: "white" }} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>{title}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          {hasDateRangeType && (
            <Typography
              variant="subtitle2"
              component="p"
              sx={{ color: "rgba(255, 0, 0, 0.6)" }}
            >
              *Note*: Only one date picker field can be selected in the section
              below. To select a different field, please remove the currently
              selected date picker field.
            </Typography>
          )}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {inputList.map((input, index) => (
              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                key={index}
                alignContent="center"
                justifySelf="center"
              >
                <Grid item xs={12} sm={5.5} md={5.5} lg={5.5}>
                  <TextField
                    select
                    key={`column-${input.columnName}-${index}`}
                    margin="normal"
                    id={`columnName${index}`}
                    label="Column Name"
                    name={`columnName${index}`}
                    size="small"
                    fullWidth
                    value={input.columnName}
                    onChange={(event: any) =>
                      handleColumnNameChange(event, index)
                    }
                  >
                    {Object.keys(clFilterableColumns).map((col) => {
                      const colType = clFilterableColumns[col].type;
                      const isCurrentColumn = col === input.columnName;
                      const isDateRangeType =
                        colType === "date-range" ||
                        colType === "datetime-range" ||
                        colType === "datetime-range-utc";
                      const isOtherColumnSelected = Object.keys(
                        filterableColumnsState
                      ).includes(col);

                      const shouldDisable =
                        (!isCurrentColumn &&
                          isDateRangeSelected &&
                          isDateRangeType) ||
                        (!isCurrentColumn && isOtherColumnSelected);

                      return (
                        <MenuItem
                          key={col}
                          value={col}
                          disabled={shouldDisable}
                        >
                          {clFilterableColumns[col].displayName}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={5.5}
                  md={5.5}
                  lg={5.5}
                  alignContent="center"
                  justifySelf="center"
                >
                  {clFilterableColumns[input.columnName] &&
                  (clFilterableColumns[input.columnName].type ===
                    "date-range" ||
                    clFilterableColumns[input.columnName].type ===
                      "datetime-range" ||
                    clFilterableColumns[input.columnName].type ===
                      "datetime-range-utc") ? (
                    <DatePicker
                      format="MM/DD/YYYY"
                      range
                      rangeHover
                      minDate={dateRange && dateRange[0]}
                      maxDate={
                        dateRange && dateRange[1] ? dateRange[1] : new Date()
                      }
                      style={{
                        width: "100%",
                        height: "35px",
                        marginTop: "9px",
                      }}
                      containerStyle={{
                        width: "100%",
                      }}
                      onChange={(dateObjects: DateObject[]) => {
                        if (dateObjects.length === 2) {
                          handleSelectedOption(
                            undefined,
                            inputList[index].columnName,
                            [
                              dateObjects[0].format("YYYY-MM-DD"),
                              dateObjects[1].format("YYYY-MM-DD"),
                            ],
                            clFilterableColumns[input.columnName].type
                          );
                        }
                      }}
                    />
                  ) : (
                    <AutoCompleteFilter
                      key={`columnNameFilter-${input.columnName}-${index}`}
                      columnName={input.columnName}
                      displayColumnName={
                        clFilterableColumns[input.columnName]
                          ? clFilterableColumns[input.columnName].displayName
                          : ""
                      }
                      options={
                        clFilterableColumns[input.columnName]
                          ? clFilterableColumns[input.columnName].options
                          : []
                      }
                      value={
                        filterableColumnsState[input.columnName]
                          ? filterableColumnsState[input.columnName].values
                          : []
                      } // Pass options prop as needed
                      onChange={
                        (event: any, selectedValue: string[]) => {
                          handleSelectedOption(
                            event,
                            inputList[index].columnName,
                            selectedValue,
                            clFilterableColumns[input.columnName].type
                          );
                        } // Pass onChange prop as needed
                      }
                    />
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={1}
                  md={1}
                  lg={1}
                  sx={{ alignSelf: "center", justifyContent: "end" }}
                >
                  <CustomTooltip title="Remove Filter">
                    <span>
                      <IconButton
                        onClick={() => handleRemoveFilter(index)}
                        disabled={inputList.length === 1}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                </Grid>
              </Grid>
            ))}
          </Box>
          <Grid container spacing={2}>
            <Grid xs={12} item display={"flex"} justifyContent="space-between">
              <Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddFilter}
                  disabled={!isLastFilterComplete()}
                >
                  Add More
                </Button>
              </Box>
              <Box>
                <Button
                  variant="contained"
                  onClick={() => {
                    handleReset(true);
                  }}
                  style={{ marginRight: "5px" }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (Object.entries(filterableColumnsState).length) {
                      asyncSubmitFunction(filterableColumnsState);
                    }
                  }}
                >
                  Submit
                </Button>
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default GridDataFilter;
